/**
 * Spec 149 — Coverage staleness helper
 *
 * A coverage analysis is only genuinely "stalled" when its orchestrator job
 * hasn't been touched for a long while. A healthy job advances roughly every
 * ~1 minute (orchestrator cadence), holds a ~3-minute lock while a phase runs,
 * and retries with a ~5-minute backoff after a transient failure. So any window
 * shorter than that is normal in-progress behaviour, NOT a stall.
 *
 * Prior surfaces flagged *every* non-terminal phase as "Stalled" (history list)
 * or every inter-tick gap as "overdue / cron may not be firing"
 * (StallDiagnostics). Both false-alarm on healthy jobs. This module is the
 * single source of truth for what "stalled" actually means.
 */

/** Phases that represent a finished run — never stalled. */
const TERMINAL_PHASES = ['completed', 'failed', 'cancelled'] as const;

/**
 * How long the orchestrator job may sit without activity before we call it
 * stalled. 10 minutes — comfortably beyond the ~1-min cadence + 3-min lock +
 * 5-min retry backoff, so a healthy job never trips it.
 */
export const COVERAGE_STALE_MS = 10 * 60 * 1000;

/**
 * True iff the analysis is genuinely stalled:
 *   - phase is NON-terminal (still claims to be working), AND
 *   - we have a real last-activity timestamp, AND
 *   - that activity is older than COVERAGE_STALE_MS.
 *
 * If `jobUpdatedAt` is null/absent we return `false` — treat an unknown
 * last-activity time as in-progress, not stalled. Reporting "stalled" on a job
 * we simply can't time is exactly the false alarm this helper exists to kill.
 */
export function isCoverageStalled(
  phase: string | null | undefined,
  jobUpdatedAt: string | null | undefined,
): boolean {
  if (phase != null && (TERMINAL_PHASES as readonly string[]).includes(phase)) {
    return false;
  }
  if (jobUpdatedAt == null) {
    return false;
  }
  const updatedMs = new Date(jobUpdatedAt).getTime();
  if (Number.isNaN(updatedMs)) {
    return false;
  }
  return Date.now() - updatedMs > COVERAGE_STALE_MS;
}
